import QuestionarioSecao from './QuestionarioSecao';

const STATUS_RASCUNHO = 'rascunho';
const STATUS_PUBLICADO = 'publicado';
const STATUS_DESATIVADO = 'desativado';

/**
 * Status possíveis do questionário
 */
const QuestionarioStatus = [ STATUS_RASCUNHO, STATUS_PUBLICADO, STATUS_DESATIVADO ];

/**
 * Objeto que representa um questionário
 */
class Questionario {
    
    /**
     * Cria uma nova instância de um questionário.
     * Caso os parâmetros não sejam informados, cria com as propriedades vazias
     * 
     * @param {Number|null} id Id do questionário na API 
     * @param {String|null} titulo Título do questionário 
     * @param {Number|null} empresa Empresa a que o questionário está vinculádo
     * @param {Boolean|null} modelo Indica se o questionário é um modelo para ser copiado
     * @param {Boolean|null} exibeCliente Indica se o questionário é visível para o cliente
     * @param {String|null} status Status do questionário
     * @param {Array<QuestionarioSecao>|null} secoes Seções realaciadas ao questionário
     */
    constructor (id, titulo, empresa, modelo, exibeCliente, status, secoes) {
        /** @member {number} id Id do questionário na API */
        this.id = id ?? null;
        /** @member {string} titulo Título do questionário */
        this.titulo = titulo ?? '';
        /** @member {number} empresa Empresa a que o questionário está vinculádo */
        this.empresa = empresa ?? 0;
        /** @member {boolean} modelo Indica se o questionário é um modelo para ser copiado */
        this.modelo = modelo ?? false;
        /** @member {boolean} exibeCliente Indica se o questionário é visível para o cliente */
        this.exibeCliente = exibeCliente ?? false;
        /** @member {string} status Status do questionário */
        this.status = status ?? STATUS_RASCUNHO;
        /** @member {Array<QuestionarioSecao>} secoes Seções realaciadas ao questionário */
        this.secoes = secoes ?? [];
    }

    /**
     * Cria uma cópia do objeto
     * @param {Questionario} origem Objeto a ser copiado
     * @returns {Questionario} Novo objeto com os mesmos valores que o original
     */
    static clone(origem) {
        //Copia a lista de seções
        let secoes = null;
        if (origem.secoes)
            secoes = origem.secoes.map(secao => QuestionarioSecao.clone(secao));
        //Cria um novo objeto com os mesmos valores
        return new Questionario(origem.id, origem.titulo, origem.empresa, origem.modelo, origem.exibeCliente, origem.status, secoes);
    }

    /**
     * Compara dois questionários, retornando verdadeiro se eles possuem os mesmos valores
     * @param {Questionario} obj1 Primeiro objeto a ser comparado
     * @param {Questionario} obj2 Segundo objeto a ser comparado
     * @returns {Boolean} Retorna verdadeiro se os dois objetos forem iguais
     */
    static compare(obj1, obj2) {
        //Se os dois possuírem o mesmo endereço de memória
        if (obj1 === obj2)
            return true;
        //Se um dos dois for nulo
        if (!obj1 || !obj2)
            return false;
        //Compara as propriedades
        let result = obj1.id === obj2.id
                  && obj1.titulo === obj2.titulo
                  && obj1.modelo === obj2.modelo
                  && obj1.exibeCliente === obj2.exibeCliente
                  && obj1.status === obj2.status
                  && obj1.ordem === obj2.ordem;

        //Caso as propriedades sejam iguais, verifica as listas
        if (result) {
            return Questionario.compareSessions(obj1.questoes, obj2.questoes);
        } else {
            return false;
        }
    }

    /**
     * Compara a lista de seções do questionário
     * @param {Array<QuestionarioSecao>} sess1 Lista de seções do primeiro objeto
     * @param {Array<QuestionarioSecao>} sess2 Lista de seções do segundo objeto
     * @returns {Boolean} Retorna verdadeiro se os itens das duas listas são iguais
     */
    static compareSessions(sess1, sess2) {
        //Se as duas listas possuírem o mesmo endereço de memória
        if (sess1 === sess2)
            return true;
        //Se uma das duas for nula
        if (!sess1 || !sess2)
            return false;
        //Se os tamanhos forem diferentes
        if (sess1.length !== sess2.length)
            return false;
        //Verifica se existe algum item que seja diferente entre as listas
        for (let i = 0; i < sess1.length; i++) {
            if (!QuestionarioSecao.compare(sess1[i], sess2[i]))
                return false;
        }
        return true;
    }

}

export default Questionario;
export { QuestionarioStatus, STATUS_RASCUNHO, STATUS_PUBLICADO, STATUS_DESATIVADO };